'use client';

import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import styles from './StaticPointMap.module.scss';
import GpsCoordinates from '@th/shared/src/types/GpsCoordinates';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_PUBLIC_ACCESS_TOKEN } from '@th/frontend-shared/src/constants/mapbox';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { twMerge } from 'tailwind-merge';

mapboxgl.accessToken = MAPBOX_PUBLIC_ACCESS_TOKEN;

type Props = {
  coordinates: GpsCoordinates;
};

const ZOOM = 14;
const PIN_COLOR = '3FB1CE'; // This is mapbox default color

export default function StaticPointMap({ coordinates }: Props) {
  const mapRef = useRef(null);
  const map = useRef(null);
  const { isDesktop } = useWindowDimensions();

  useEffect(() => {
    if (
      map.current &&
      map.current.getCenter().lng == coordinates.longitude &&
      map.current.getCenter().lat == coordinates.latitude
    ) {
      // initialize map only once
      return;
    }

    map.current = new mapboxgl.Map({
      container: mapRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [coordinates.longitude, coordinates.latitude],
      zoom: ZOOM,
      attributionControl: false,
      interactive: isDesktop,
      maxZoom: 16,
      minZoom: 9,
    });

    new mapboxgl.Marker()
      .setLngLat([coordinates.longitude, coordinates.latitude])
      .addTo(map.current);

    map.current.on('load', () => {
      mapRef.current.style.visibility = 'visible';
    });
  }, [coordinates.longitude, coordinates.latitude, isDesktop]);

  // We improve perceived performance and First Contentful Paint (FCP) by rendering a static map image, then switching it out once mapbox has loaded
  // See: https://docs.mapbox.com/help/tutorials/improve-perceived-performance-with-static/
  // Note: 864 x 384 corresponds to max-width of body (which is set way up in the parent), and max height of the map (which is set here: tailwind h-96 == 24em == 384 px)
  //       Because we center background image, this will still fit and look good when the map container is smaller

  return (
    <div className={twMerge(styles.container, 'h-40 md:h-96 z-0 grid')}>
      <div id={'map'} ref={mapRef} className={styles.map} />
      <div
        id={'static'}
        className={styles.static}
        style={{
          backgroundImage: `url("https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-l+${PIN_COLOR}(${coordinates.longitude},${coordinates.latitude})/${coordinates.longitude},${coordinates.latitude},${ZOOM},0/864x384?access_token=${MAPBOX_PUBLIC_ACCESS_TOKEN}&logo=false&attribution=false")`,
        }}
      />
    </div>
  );
}
